import Component from '@glimmer/component';
import {
  PrismIcon,
  PrismIconBrand,
  PrismIconFlag,
} from '@mylighthouse/prism-ember';
import type { IconBrandName } from '@mylighthouse/prism-ember/types/components/prism/icon-brand';
import type { IconFlagCode } from '@mylighthouse/prism-ember/types/components/prism/icon-flag';
import { eq } from 'ember-truth-helpers';
import { Illustration } from 'frontend-utils';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import BookingGeniusIllustration from 'lumen/assets/images/member-rates/booking-genius.svg?raw';
import ExpediaRewardsIllustration from 'lumen/assets/images/member-rates/expedia-rewards.svg?raw';
import { MEALTYPE_ICONS } from 'lumen/utils/constants/general-constants';

interface HotelStrategySubtypeIconSignature {
  Args: {
    isBranddotcomMemberRatesSubType: boolean;
    isMealtypeType: boolean;
    isMemberRatesType: boolean;
    isMobileBookingSubtype: boolean;
    isMobileExpediaSubtype: boolean;
    isMobileHotelsDotComSubtype: boolean;
    isPosBookingSubtype: boolean;
    isPosExpediaSubtype: boolean;
    label: string | number;
    name: string;
  };
}

export default class HotelStrategySubtypeIcon extends Component<HotelStrategySubtypeIconSignature> {
  get countryCode(): IconFlagCode | '' {
    if (typeof this.args.label === 'string') {
      return this.args.label.toLowerCase() as IconFlagCode;
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mealtypeIconName() {
    return MEALTYPE_ICONS[this.args.label as keyof typeof MEALTYPE_ICONS];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get memberRatesTypeOtaIconName() {
    if (this.args.isBranddotcomMemberRatesSubType) {
      return OTA_IDS.BRANDDOTCOM;
    }

    if (typeof this.args.label === 'string') {
      return this.args.label as IconBrandName;
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mobileOtaTypeIconName() {
    if (this.args.isMobileBookingSubtype) {
      return OTA_IDS.BOOKINGDOTCOM;
    }

    if (this.args.isMobileExpediaSubtype) {
      return OTA_IDS.EXPEDIA;
    }

    if (this.args.isMobileHotelsDotComSubtype) {
      return OTA_IDS.HOTELSDOTCOM;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get posOtaTypeIconName() {
    if (this.args.isPosBookingSubtype) {
      return OTA_IDS.BOOKINGDOTCOM;
    }

    if (this.args.isPosExpediaSubtype) {
      return OTA_IDS.EXPEDIA;
    }

    return null;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if this.mobileOtaTypeIconName}}
      <PrismIconBrand @name={{this.mobileOtaTypeIconName}} />
    {{else if @isMealtypeType}}
      {{#if this.mealtypeIconName}}
        <PrismIcon @name={{this.mealtypeIconName}} @size="small" />
      {{/if}}
    {{else if this.posOtaTypeIconName}}
      <PrismIconBrand @name={{this.posOtaTypeIconName}} />
      {{#if this.countryCode}}
        <PrismIconFlag @code={{this.countryCode}} />
      {{/if}}
    {{else if @isMemberRatesType}}
      <div class="flex items-center gap-100">
        {{#if this.memberRatesTypeOtaIconName}}
          <PrismIconBrand @name={{this.memberRatesTypeOtaIconName}} />
        {{/if}}
        {{#if (eq this.memberRatesTypeOtaIconName OTA_IDS.BRANDDOTCOM)}}
          <p class="text-300--bold">Brand.com</p>
        {{/if}}
        {{#if (eq this.memberRatesTypeOtaIconName OTA_IDS.BOOKINGDOTCOM)}}
          <Illustration
            @html={{BookingGeniusIllustration}}
            aria-label="Booking Genius icon"
          />
        {{/if}}
        {{#if (eq this.memberRatesTypeOtaIconName OTA_IDS.EXPEDIA)}}
          <Illustration
            @html={{ExpediaRewardsIllustration}}
            aria-label="Expedia Rewards icon"
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
